/**
 * Conversation Service — client↔team threads.
 *
 * Reads are direct Firestore listeners; every write except the caller's own
 * readAt marker goes through a callable (see functions/conversations/).
 */

import { db, auth, functions } from '@/js/firebase'
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { uploadRawFile } from './bunnyUploadService'
import { BUNNY_UPLOAD_CONTEXT, BUNNY_APP_NAMESPACE } from '@/utils/bunnyConstants'
import { logger } from './loggerService'

const call = (name, data) => httpsCallable(functions, name)(data).then((r) => r.data)
const withId = (snap) => ({ id: snap.id, ...snap.data() })

/** Conversations for one company, newest activity first. */
export function subscribeCompanyConversations(companyId, onChange, onError) {
  const q = query(
    collection(db, 'conversations'),
    where('companyId', '==', companyId),
    orderBy('lastMessageAt', 'desc')
  )
  return onSnapshot(q, (snap) => onChange(snap.docs.map(withId)), (err) => {
    logger.error('[ConversationService] Company listener failed', { companyId, error: err.message })
    onError?.(err)
  })
}

/** Staff inbox: every conversation, newest activity first. */
export function subscribeAllConversations(onChange, onError, max = 300) {
  // ponytail: single capped listener; paginate once volume passes a few hundred threads.
  const q = query(collection(db, 'conversations'), orderBy('lastMessageAt', 'desc'), limit(max))
  return onSnapshot(q, (snap) => onChange(snap.docs.map(withId)), (err) => {
    logger.error('[ConversationService] Inbox listener failed', { error: err.message })
    onError?.(err)
  })
}

/** Count of unanswered (New) conversations — drives the admin nav dot. */
export function subscribeNewCount(onChange) {
  const q = query(collection(db, 'conversations'), where('status', '==', 'new'))
  return onSnapshot(q, (snap) => onChange(snap.size), (err) => {
    logger.warn('[ConversationService] New-count listener failed', { error: err.message })
  })
}

export function subscribeConversation(conversationId, onChange) {
  return onSnapshot(doc(db, 'conversations', conversationId), (snap) =>
    onChange(snap.exists() ? withId(snap) : null)
  )
}

export function subscribeMessages(conversationId, onChange) {
  const q = query(
    collection(db, 'conversations', conversationId, 'messages'),
    orderBy('createdAt', 'asc')
  )
  return onSnapshot(q, (snap) => onChange(snap.docs.map(withId)))
}

/**
 * Staff only — email events (sends + SendGrid delivery status). Clients can't
 * read conversations/{id}/events and must never call this.
 */
export function subscribeEvents(conversationId, onChange) {
  const q = query(
    collection(db, 'conversations', conversationId, 'events'),
    orderBy('createdAt', 'asc')
  )
  return onSnapshot(q, (snap) => onChange(snap.docs.map(withId)), (err) => {
    logger.warn('[ConversationService] Events listener failed', { conversationId, error: err.message })
  })
}

/**
 * @param {Object} payload - { companyId, category, billingType?, subject, body,
 *   attachments?, link?, participantIds? (staff-started), previousConversationId? }
 * @returns {Promise<string>} conversationId
 */
export async function createConversation(payload) {
  const { conversationId } = await call('createConversation', payload)
  logger.info('[ConversationService] Created', { conversationId, companyId: payload.companyId })
  return conversationId
}

export const postMessage = (conversationId, body, attachments = []) =>
  call('postConversationMessage', { conversationId, body, attachments })

/** action: setStatus {status} | assign {assigneeId} | claim | addParticipant {userId} | reopen | rename {subject} */
export const updateConversation = (conversationId, action, extra = {}) =>
  call('updateConversation', { conversationId, action, ...extra })

/** Staff only. */
export const renameConversation = (conversationId, subject) => updateConversation(conversationId, 'rename', { subject })

const CHECK_TIMEOUT_MS = 4000

/**
 * Send check for a new client conversation. Fail-open: resolves null on any
 * error or after 4s, and the caller sends without it.
 * @param {Object} payload - { companyId, category, billingType?, link?, body }
 * @returns {Promise<{ checkId, subject, subjectSource, suggestion, category, link }|null>}
 */
export function checkDraft(payload) {
  let timer = null
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => {
      logger.warn('[ConversationService] Draft check timed out', { companyId: payload.companyId })
      resolve(null)
    }, CHECK_TIMEOUT_MS)
  })
  const check = call('checkConversationDraft', payload).catch((err) => {
    logger.warn('[ConversationService] Draft check failed', { companyId: payload.companyId, error: err.message })
    return null
  })
  return Promise.race([check, timeout]).finally(() => clearTimeout(timer))
}

/** The client took the suggestion instead of sending. Best-effort. */
export const recordCheckOutcome = (checkId) =>
  call('recordConversationCheckOutcome', { checkId }).catch((err) => {
    logger.warn('[ConversationService] Check outcome not recorded', { checkId, error: err.message })
  })

/**
 * Staff only — clients can't read conversationMoods and must never call this.
 * @param {(moods: Object<string, Object>) => void} onChange - map keyed by conversation id
 */
export function subscribeMoods(onChange) {
  return onSnapshot(
    collection(db, 'conversationMoods'),
    (snap) => onChange(Object.fromEntries(snap.docs.map((d) => [d.id, d.data()]))),
    (err) => logger.warn('[ConversationService] Mood listener failed', { error: err.message })
  )
}

export async function markRead(conversationId) {
  const uid = auth.currentUser?.uid
  if (!uid) return
  try {
    await updateDoc(doc(db, 'conversations', conversationId), { [`readAt.${uid}`]: serverTimestamp() })
  } catch (err) {
    logger.warn('[ConversationService] markRead failed', { conversationId, error: err.message })
  }
}

/** Unread for the current user: last message came from the other side after they last read. */
export function isUnread(conversation, uid, isStaff) {
  // Manually marked unread (markUnread writes the epoch): unread whoever spoke last.
  const marker = conversation.readAt?.[uid]
  if (marker && marker.toMillis?.() === 0) return true
  const otherSide = isStaff ? 'client' : 'staff'
  if (conversation.lastMessageBy !== otherSide) return false
  const readAt = conversation.readAt?.[uid]?.toMillis?.() || 0
  const lastAt = conversation.lastMessageAt?.toMillis?.() || 0
  return lastAt > readAt
}

/**
 * Per-user "mark as unread": sets this user's own read marker to the epoch.
 * Only touches readAt.<uid>, which the rules let every member write.
 */
export async function markUnread(conversationId) {
  const uid = auth.currentUser?.uid
  if (!uid) return
  await updateDoc(doc(db, 'conversations', conversationId), { [`readAt.${uid}`]: Timestamp.fromMillis(0) })
}

/** Inbox order: unread first (newest first), then everything else by latest activity. */
export function sortForInbox(list, uid, isStaff) {
  const last = (c) => c.lastMessageAt?.toMillis?.() || 0
  return [...list].sort((a, b) => {
    const ua = isUnread(a, uid, isStaff)
    const ub = isUnread(b, uid, isStaff)
    if (ua !== ub) return ua ? -1 : 1
    return last(b) - last(a)
  })
}

/** contextId groups uploads by company; conversation ids don't exist before create. */
export async function uploadAttachment(companyId, file) {
  const result = await uploadRawFile({
    file,
    context: {
      type: BUNNY_UPLOAD_CONTEXT.TEMP_ATTACHMENT,
      namespace: BUNNY_APP_NAMESPACE.CONVERSATION,
      contextId: companyId
    }
  })
  return {
    type: file.type?.startsWith('image/') ? 'image' : 'file',
    url: result.url || null, // signed + expiring; render from storagePath
    storagePath: result.path,
    fileName: file.name,
    contentType: file.type,
    size: file.size
  }
}

/** Any non-storage contract counts as an Anthem contract (UVP included). Mirrors the callable. */
export async function hasAnthemContract(companyId) {
  const snap = await getDocs(query(collection(db, 'contracts'), where('companyId', '==', companyId)))
  return snap.docs.some((d) => d.data().type !== 'storage')
}

// --- Staff settings (settings/conversations) --------------------------------

/** Routing + SLA config. Admins write settings/* directly per firestore.rules. */
export async function getSettings() {
  const { getDoc } = await import('firebase/firestore')
  const snap = await getDoc(doc(db, 'settings', 'conversations'))
  return snap.exists() ? snap.data() : {}
}

export async function saveSettings(settings) {
  const { setDoc } = await import('firebase/firestore')
  await setDoc(doc(db, 'settings', 'conversations'), { ...settings, updatedAt: serverTimestamp() }, { merge: true })
  logger.info('[ConversationService] Settings saved')
}
